import { datadogRum } from '@datadog/browser-rum';
import { readCookie } from '@zapier/cookies';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { createContext, useContext, useState } from 'react';
import type { AlertBanner, PageContent } from '../builder';
import { useDeepCompareEffect } from '../hooks/useDeepCompareEffect';
import { TrackRouting } from '../observability';
import { FallbackPage } from './FallbackPage';
import { Meta } from './Meta/Meta';
import { PageBuilderComponent } from './PageBuilderComponent';
import { UniversalLayout } from './UniversalLayout/UniversalLayout';
import { WistiaControlScript } from './WistiaVideo/WistiaControlScript';

const Survey = dynamic<any | never>(() =>
  import('./Survey/Survey').then((mod) => mod.SurveyContainer),
);

export type PageData = PageContent['data'] & {
  alertBanner: AlertBanner & {
    /**
     * If the alert banner has a body with data binding, then it must be parsed
     * in order for the asyncData values to be injected.
     * parsedBody stores that value. We don't overwrite alertBanner.body with the parsed value
     * because we need to keep the original in the event that the asyncData values change.
     */
    parsedBody?: string;
  };
};

type Props = {
  content: PageContent;
};

type PageContextValues = {
  id?: string;
  variations?: PageContent['variations'];
  name?: string;
};

const PageContext = createContext<PageContextValues>({});

export const usePageContext = () => useContext(PageContext);

export function getActivePageVariantName(
  variations: PageContextValues['variations'],
  id: PageContextValues['id'],
): string | null {
  if (!id || !variations || Object.keys(variations).length == 0) {
    return null;
  }

  const variantID = readCookie(`builder.tests.${id}`);
  if (!variantID) {
    return null;
  }

  return variations?.[variantID]?.name ?? 'control';
}

export function Page({ content }: Props) {
  const [pageData, setPageData] = useState<PageData>(content?.data as PageData);
  const [isIntercomExperiment, setIsIntercomExperiment] = useState(false);

  const router = useRouter();

  useDeepCompareEffect(() => {
    const variant = getActivePageVariantName(content?.variations, content?.id);
    if (!variant) {
      return;
    }

    /* 
      DataDog disallows most special characters in feature flag names,
      so any character that is not a letter, number, or underscore gets 
      replaced with an underscore
    */
    const name = `pagebuilder_${content.name?.replace(/[^a-zA-Z_0-9]/g, '_')}`;
    datadogRum.addFeatureFlagEvaluation(name, variant);

    // TODO: remove after Intercom experiment on the homepage
    if (window.location.pathname === '/' && variant === 'treatment') {
      setIsIntercomExperiment(true);
    }
  }, [content?.variations, content?.id, content?.name]);

  // https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
  if (router.isFallback) {
    return <FallbackPage />;
  }

  const {
    alertBanner,
    articleStructuredData,
    description,
    openGraphImageUrl,
    openToSearchEngines,
    showSurvey,
    title,
    universalFooter,
    universalLayout,
  } = pageData || {};

  return (
    <PageContext.Provider
      value={{
        id: content.id,
        variations: content.variations,
        name: content.name,
      }}
    >
      <UniversalLayout
        alertBanner={{
          ...alertBanner,
          body: alertBanner?.parsedBody || alertBanner?.body,
        }}
        hideFooter={universalLayout?.hideFooter}
        hideHeader={universalLayout?.hideHeader}
        shouldRenderAlphabetNav={universalFooter?.shouldRenderAlphabetNav}
        shouldRenderSeoLinks={universalFooter?.shouldRenderSeoLinks}
      >
        <TrackRouting />
        <Meta
          articleStructuredData={articleStructuredData}
          description={description}
          openGraphImageUrl={openGraphImageUrl}
          openToSearchEngines={openToSearchEngines}
          title={title}
        />
        <WistiaControlScript />
        <PageBuilderComponent content={content} setPageData={setPageData} />
        {showSurvey && !isIntercomExperiment && <Survey />}
      </UniversalLayout>
    </PageContext.Provider>
  );
}
